import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding, useOnboardingProgress } from '../../hooks/onboarding';
import { OnboardingProgress } from './OnboardingProgress';
import { OnboardingNavigation } from './OnboardingNavigation';
import { OnboardingContainer as Container, StepContainer } from '../../styles/onboarding/Onboarding.styles';
import { logger } from '../../utils/logger';
import { onboardingStorage, personaService } from '../../services/Onboarding';
import { profileService } from '../../services/Profile/profileService';
import { secureStorage } from '../../services/Storage/secureStorage';
import { BasicInfo, Bio } from '../../components/Profile/types';
import { OnboardingStepId } from '../../types/onboarding';

// Step Components
import { WelcomeStep } from './steps/WelcomeStep';
import { AssessmentStep } from './steps/AssessmentStep';
import { PersonaConfirmationStep } from './steps/PersonaConfirmationStep';
import { PhotosStep } from './steps/PhotosStep';
import { ProfileStep } from './steps/ProfileStep';
import { InterestsStep } from './steps/InterestsStep';
import { PreferencesStep } from './steps/PreferencesStep';
import { AppTourStep } from './steps/AppTourStep';
import { CompletionStep } from './steps/CompletionStep';

interface OnboardingContainerProps {
  /** Open directly on this step (used by the feed's completion prompt). */
  initialStepId?: OnboardingStepId;
  /**
   * Called instead of navigating to /social-feed when the flow finishes.
   * Supplied when onboarding runs as a modal over a page that should stay put.
   */
  onFinish?: () => void;
  /**
   * Hands the host a function that saves whatever step the user is currently
   * on. The modal calls it before closing, because otherwise filling in a step
   * and closing with × threw the answer away: persistence only ever ran on
   * "Next", and for a one-step prompt opened from the feed, closing IS how
   * people finish.
   */
  onRegisterPersist?: (persist: () => Promise<void>) => void;
}

/**
 * OnboardingContainer - Clean Redesign
 * The orchestrator for the entire onboarding experience.
 */
export const OnboardingContainer: React.FC<OnboardingContainerProps> = ({
  initialStepId,
  onFinish,
  onRegisterPersist,
}) => {
  const {
    currentStepId,
    currentStep,
    isFirstStep,
    isLastStep,
    saveStepData,
    getStepData,
    goToNextStep,
    goToPreviousStep,
    skipToNextStep,
    completeOnboarding
  } = useOnboarding(initialStepId);
  const { percentage: onboardingPercentage } = useOnboardingProgress();
  const navigate = useNavigate();

  // Local validation and overrides
  const [isStepValid, setIsStepValid] = useState(true);
  const [nextOverride, setNextOverride] = useState<(() => void) | null>(null);
  const [backOverride, setBackOverride] = useState<(() => void) | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  // 1. Reset state on step change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    setIsStepValid(true);
    setNextOverride(null);
    setBackOverride(null);
    setSyncError(null);
  }, [currentStepId]);

  // 1b. Prefill every step from the user's saved backend profile, on EVERY
  // open of the flow — not once per device as before. Re-entering onboarding
  // (e.g. from the Matches feed's completion prompt) must reflect whatever is
  // currently stored, including anything saved since from Edit Profile or on
  // another device, or the user is asked for the same information twice.
  // Merges fetched values into only the fields that are still empty, so it
  // never clobbers anything the user has already typed in this session.
  useEffect(() => {
    (async () => {
      try {
        const profile = await profileService.getProfile();
        const { basicInfo, bio } = profile;

        const existingProfileStep = onboardingStorage.getStepData('profile') || {};
        onboardingStorage.saveProgress('profile', {
          bio: existingProfileStep.bio?.content ? existingProfileStep.bio : (bio?.content ? bio : (existingProfileStep.bio || { content: '', characterCount: 0 })),
          occupation: existingProfileStep.occupation || basicInfo.occupation || '',
          education: existingProfileStep.education || basicInfo.education || '',
          location: {
            city: existingProfileStep.location?.city || basicInfo.location?.city || '',
            state: existingProfileStep.location?.state || basicInfo.location?.state || '',
            country: existingProfileStep.location?.country || basicInfo.location?.country || '',
          },
        });

        const existingInterestsStep = onboardingStorage.getStepData('interests') || {};
        if (!existingInterestsStep.interests?.length && profile.interests?.length) {
          onboardingStorage.saveProgress('interests', { interests: profile.interests });
        }

        const existingAssessmentStep = onboardingStorage.getStepData('assessment') || {};
        if (!existingAssessmentStep.selectedPersona) {
          const user = secureStorage.getUserData();
          const personaData = user?.id ? await personaService.getUserPersonaData(user.id) : null;
          if (personaData?.primaryPersona) {
            onboardingStorage.saveProgress('assessment', {
              ...existingAssessmentStep,
              selectedPersona: personaData.primaryPersona,
              personaTraits: personaData.traits,
            });
          }
        }

        // Preferences were never prefilled (nor even saved) before, so this
        // step always reopened at its hardcoded 18-99 / 50mi defaults.
        const existingPrefsStep = onboardingStorage.getStepData('preferences') || {};
        if (!existingPrefsStep.preferences) {
          const discovery = await profileService.getDiscoverySettings('').catch(() => null);
          onboardingStorage.saveProgress('preferences', {
            preferences: {
              ageRange: discovery?.ageRange ?? { min: 18, max: 99 },
              maxDistance: discovery?.distance ?? 50,
              showMe: discovery?.genderPreference ?? [],
              lookingFor: basicInfo.lookingFor || '',
            },
          });
        }
      } catch (err) {
        // Best-effort prefill only — a brand-new user may not have a profile
        // row yet, which is fine; onboarding just starts blank in that case.
        logger.log('[ONBOARDING] Skipping prefill (no existing profile yet):', err);
      }
    })();
  }, []);

  // 2. Core Handlers
  const handleDataChange = useCallback((data: any, isValid: boolean = true) => {
    saveStepData(currentStepId, data);
    setIsStepValid(isValid);
  }, [currentStepId, saveStepData]);

  // Push the step the user is leaving straight to the backend. Previously
  // nothing was written until the final step, so abandoning the flow halfway
  // lost everything typed — and re-opening it asked for all of it again.
  // Failures are logged, not blocking: localStorage still holds the data and
  // the final sync retries it, so a flaky request never traps the user.
  const persistStep = useCallback(async (stepId: OnboardingStepId) => {
    const userId = secureStorage.getUserData()?.id;
    if (!userId) return;

    const stepData = onboardingStorage.getStepData(stepId) || {};

    try {
      switch (stepId) {
        case 'profile': {
          const basicInfoUpdate: Partial<BasicInfo> = {};
          if (stepData.occupation) basicInfoUpdate.occupation = stepData.occupation;
          if (stepData.education) basicInfoUpdate.education = stepData.education;
          if (stepData.location?.city || stepData.location?.state
              || stepData.location?.country || stepData.location?.coordinates) {
            basicInfoUpdate.location = {
              city: stepData.location.city || '',
              state: stepData.location.state || '',
              country: stepData.location.country || '',
              // Carried through when the browser granted geolocation. Dropping
              // them here would leave the backend geocoding a city it did not
              // need to, and the user's precise answer thrown away.
              coordinates: stepData.location.coordinates,
            };
          }
          const payload: { basicInfo?: BasicInfo; bio?: Bio } = {};
          if (Object.keys(basicInfoUpdate).length) payload.basicInfo = basicInfoUpdate as BasicInfo;
          if (stepData.bio?.content) payload.bio = stepData.bio as Bio;
          if (Object.keys(payload).length) await profileService.updateProfile(userId, payload);
          break;
        }
        case 'interests': {
          if (stepData.interests?.length) {
            await profileService.updateProfile(userId, { interests: stepData.interests });
          }
          break;
        }
        case 'preferences': {
          const p = stepData.preferences;
          if (p) {
            await profileService.updateDiscoverySettings('', {
              ageRange: p.ageRange,
              distance: p.maxDistance,
              genderPreference: p.showMe ?? [],
              interestedIn: p.lookingFor ? [p.lookingFor] : [],
              dealBreakers: [],
            } as any);
            // looking_for lives on the user row, not user_preferences.
            if (p.lookingFor) {
              await profileService.updateProfile(userId, {
                basicInfo: { lookingFor: p.lookingFor } as BasicInfo,
              });
            }
          }
          break;
        }
        case 'assessment':
        case 'persona-confirmation': {
          const persona = stepData.selectedPersona || stepData.persona?.selectedPersona;
          if (persona) {
            await personaService.updateUserPersona(
              userId,
              persona,
              stepData.personaTraits || [],
              stepData.personaConfidence || 0
            );
          }
          break;
        }
        // 'photos' persists at upload time; welcome/app-tour/completion carry
        // no server-side data.
        default:
          break;
      }
      logger.log(`[ONBOARDING] Persisted step: ${stepId}`);
    } catch (err) {
      logger.error(`[ONBOARDING] Could not persist step ${stepId}:`, err);
    }
  }, []);

  // Hand the saver to the host. Kept in an effect rather than called during
  // render so the host is never updated mid-render.
  useEffect(() => {
    if (!onRegisterPersist) return;
    onRegisterPersist(() => persistStep(currentStepId));
  }, [onRegisterPersist, persistStep, currentStepId]);

  const handleNext = useCallback(async () => {
    if (nextOverride) {
      nextOverride();
      return;
    }

    if (isLastStep) {
      try {
        const allData = onboardingStorage.getAllOnboardingData();
        const user = secureStorage.getUserData();

        if (user?.id) {
          // Only include fields the user actually entered — a skipped step
          // (e.g. "profile" or "interests" is optional and can be skipped)
          // falls back to empty defaults in getAllOnboardingData(), and
          // sending those through would overwrite real data already saved
          // via Edit Profile with blanks.
          const basicInfoUpdate: Partial<BasicInfo> = {};
          if (allData.occupation) basicInfoUpdate.occupation = allData.occupation;
          if (allData.education) basicInfoUpdate.education = allData.education;
          if (allData.location && (allData.location.city || allData.location.state
              || allData.location.country || allData.location.coordinates)) {
            basicInfoUpdate.location = {
              city: allData.location.city || '',
              state: allData.location.state || '',
              country: allData.location.country || '',
              coordinates: allData.location.coordinates,
            };
          }

          const updatePayload: { basicInfo?: BasicInfo; bio?: Bio; interests?: typeof allData.interests } = {};
          if (Object.keys(basicInfoUpdate).length > 0) updatePayload.basicInfo = basicInfoUpdate as BasicInfo;
          if (allData.bio?.content) updatePayload.bio = allData.bio as Bio;
          if (allData.interests?.length) updatePayload.interests = allData.interests;

          if (Object.keys(updatePayload).length > 0) {
            logger.log('[ONBOARDING] Finalizing profile sync...');
            await profileService.updateProfile(user.id, updatePayload);
          }

          if (allData.persona?.selectedPersona) {
            await personaService.updateUserPersona(
              user.id,
              allData.persona.selectedPersona,
              allData.persona.personaTraits || [],
              allData.persona.personaConfidence || 0
            );
          }
        }
        // Only mark onboarding complete server-side once every trackable
        // step has actually been touched (completed or skipped) — matches
        // the same 100% definition shown on the completion screen itself.
        if (onboardingPercentage === 100) {
          await profileService.completeOnboarding();
        }

        // The backend write is the source of truth. Only mark this device's
        // local cache complete and navigate away once the server calls above
        // actually succeeded — otherwise the DB's onboarding_completed can
        // stay false while this browser thinks it's done, and the user gets
        // bounced back into onboarding on their next device/session.
        completeOnboarding();
        // Running as a modal: hand control back to the host page instead of a
        // full navigation, which would throw away the feed underneath.
        if (onFinish) {
          onFinish();
        } else {
          // Client-side navigation, NOT window.location.href. A hard reload
          // tears down the in-memory auth state and the feed renders its
          // "please log in" branch for the moment it takes session restore to
          // finish — the user is logged in the whole time.
          navigate('/social-feed');
        }
      } catch (err) {
        logger.error('[ONBOARDING] Sync error:', err);
        setSyncError('Something went wrong finishing setup. Please try again.');
      }
      return;
    }

    if (isStepValid) {
      await persistStep(currentStepId);
      goToNextStep();
    }
  }, [isLastStep, isStepValid, nextOverride, completeOnboarding, goToNextStep, onboardingPercentage, persistStep, currentStepId, onFinish, navigate]);

  const handleBack = useCallback(() => {
    if (backOverride) {
      backOverride();
      return;
    }
    goToPreviousStep();
  }, [backOverride, goToPreviousStep]);

  const handleSkip = useCallback(() => {
    skipToNextStep();
  }, [skipToNextStep]);

  // 3. Step Rendering Boilerplate
  const renderStep = () => {
    const props = {
      data: getStepData(currentStepId) || {},
      onChange: handleDataChange,
      onNext: handleNext,
      onBack: handleBack,
      onSkip: currentStep.canSkip ? handleSkip : undefined,
      setNextOverride,
      setBackOverride,
      isFirstStep,
      isLastStep
    };

    switch (currentStepId) {
      case 'welcome': return <WelcomeStep {...props} />;
      case 'assessment': return <AssessmentStep {...props} />;
      case 'persona-confirmation': return <PersonaConfirmationStep {...props} />;
      case 'photos': return <PhotosStep {...props} />;
      case 'profile': return <ProfileStep {...props} />;
      case 'interests': return <InterestsStep {...props} />;
      case 'preferences': return <PreferencesStep {...props} />;
      case 'app-tour': return <AppTourStep {...props} />;
      case 'completion': return <CompletionStep {...props} />;
      default: return <WelcomeStep {...props} />;
    }
  };

  return (
    <Container>
      <OnboardingProgress />

      <StepContainer key={currentStepId}>
        {renderStep()}
      </StepContainer>

      {syncError && (
        <div
          role="alert"
          style={{ color: '#d64545', textAlign: 'center', padding: '0 16px 8px', fontSize: '0.9rem' }}
        >
          {syncError}
        </div>
      )}

      <OnboardingNavigation
        onNext={handleNext}
        onBack={handleBack}
        onSkip={currentStep.canSkip ? handleSkip : undefined}
        isFirstStep={isFirstStep && !backOverride}
        isLastStep={isLastStep}
        canSkip={currentStep.canSkip}
        isNextDisabled={!isStepValid && !currentStep.canSkip && !nextOverride}
        nextButtonText={isLastStep ? 'Start Exploring' : (nextOverride ? 'Continue' : 'Next')}
      />
    </Container>
  );
};
